import React, { useState } from 'react';
import SimpleActionBar from '@components/menu/SimpleActionBar';
import { IEnhancedMenuItem } from '@components/table/types';
import { IAmendmentPageData } from '../..';
import { IAmendmentGeneralData } from '../../line-amendment';
import { LineCategory } from '@utils/helper-utils';
import { DocumentNode } from 'graphql';
import {
  calculateAmendmentExpat,
  calculateAmendmentGeneric,
  calculateAmendmentMedical,
  calculateAmendmentMotor,
  calculateAmendmentTravel,
  cancelAmendmentMutation,
  cancelPolicyCancelationAmendmentMutation,
  confirmCancellationAmendmentMutation,
} from './queries';
import { IAbstractRecord } from '@models';
import { getMotorCalculateVariables } from './utils';
import { ApolloError, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { getError } from '@utils/graph-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { isEmpty } from '@utils/validationUtils';
import PreviewClauseModal from '../../components/preview-clause-modal/PreviewClauseModal';
import { confirmAmendmentMutation } from '../../components/preview-clause-modal/queries';
import { useAppDispatch } from '@redux/hooks';
import { OpenConfirmationAction } from '@redux/confirmation/actions';
import { IConfirmation } from '@redux/confirmation/types';
import { useParams } from 'react-router-dom';
import DateService from '@services/DateService';
import { validateMotorVignette } from '../../queries';

interface IStepperActions {
  generalData: IAmendmentGeneralData;
  values: IAmendmentPageData;
  onSuccess: () => void;
}

const lineCategoryCalculation: Record<LineCategory, DocumentNode> = {
  Motor: calculateAmendmentMotor,
  Expat: calculateAmendmentExpat,
  Generic: calculateAmendmentGeneric,
  Medical: calculateAmendmentMedical,
  Travel: calculateAmendmentTravel,
};

const lineCategoryVariablesBuilder: Record<
  LineCategory,
  (
    values: IAmendmentPageData,
    generalData: IAmendmentGeneralData
  ) => IAbstractRecord
> = {
  Motor: getMotorCalculateVariables,
  Expat: () => ({}),
  Generic: () => ({}),
  Medical: () => ({}),
  Travel: () => ({}),
};

const StepperActions: React.FC<IStepperActions> = ({
  generalData,
  values,
  onSuccess,
}) => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [calculateAndSave, { loading: calculateLoading }] = useMutation(
    lineCategoryCalculation[generalData.line.category]
  );
  const [confirmAmendment, { loading: confirmLoading }] = useMutation(
    confirmAmendmentMutation
  );
  const [
    confirmCancellationAmendment,
    { loading: confirmCancellationLoading },
  ] = useMutation(confirmCancellationAmendmentMutation);
  const [cancelAmendment, { loading: cancelLoading }] = useMutation(
    generalData.amendment.type === 'POLICY_CANCELLATION'
      ? cancelPolicyCancelationAmendmentMutation
      : cancelAmendmentMutation
  );
  const [validateVignetteLazy] = useMutation(validateMotorVignette);

  const handleCalculateAndSave = async () => {
    try {
      const mappedVariables = lineCategoryVariablesBuilder[
        generalData.line.category
      ](values, generalData);

      if (generalData.line.externalCode === '50') {
        try {
          await validateVignetteLazy({
            variables: {
              businessPartnerID: generalData.primaryBP.id,
              entityId: params.id,
              effectiveDate: DateService.formatDateBackend(
                generalData.policy.effectiveDate
              ),
              vignetteCode: mappedVariables.vignetteCode,
              vignette: Number(mappedVariables.vignette),
              policyID: generalData.policy.id,
            },
          });
        } catch (error) {
          if (
            error instanceof ApolloError &&
            error.message === 'Vignette Number is not assigned to you'
          ) {
            toast.error(
              <ToastErrorMessage>
                Vignette Number not assigned to you.
              </ToastErrorMessage>
            );
            return; // Stop execution
          } else if (
            error instanceof ApolloError &&
            error.message === 'Vignette already in use'
          ) {
            toast.error(
              <ToastErrorMessage>Vignette already in use.</ToastErrorMessage>
            );
            return; // Stop execution
          }
        }
      }

      const { errors } = await calculateAndSave({
        variables: mappedVariables,
        errorPolicy: 'all',
      });

      if (isEmpty(errors)) {
        toast.success(
          <ToastSuccessMessage>
            Amendment successfully saved
          </ToastSuccessMessage>
        );
        onSuccess && onSuccess();
      } else {
        toast.error(
          <ToastErrorMessage>{getError({ errors })}</ToastErrorMessage>
        );
      }
    } catch (error) {
      toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
    }
  };

  const handleConfirmAmendment = async () => {
    if (['50', '43'].includes(generalData.line.externalCode)) {
      try {
        const { errors } = await confirmAmendment({
          variables: {
            selectedAmendmentID: generalData.amendment.id,
            selectedPolicyVersionID: generalData.policy.versionId,
            selectedPolicyID: generalData.policy.id,
            clauseText: generalData.policy.clauseText,
          },
          errorPolicy: 'all',
        });

        if (isEmpty(errors)) {
          toast.success(
            <ToastSuccessMessage>
              Amendment successfully confirmed
            </ToastSuccessMessage>
          );
          onSuccess();
        } else {
          toast.error(
            <ToastErrorMessage>{getError({ errors })}</ToastErrorMessage>
          );
        }
      } catch (error) {
        toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
      }
    } else {
      setIsModalOpen(true);
    }
  };

  const handleCancellationConfirmAmendment = async () => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirm Cancellation',
      message: 'Are you sure you want to proceed with canceling the policy?',
      submitButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      callback: async () => {
        try {
          const res = await confirmCancellationAmendment({
            variables: {
              selectedAmendmentID: generalData.amendment.id,
            },
            errorPolicy: 'all',
          });

          if (!res.errors) {
            toast.success(
              <ToastSuccessMessage>
                Amendment successfully confirmed
              </ToastSuccessMessage>
            );
            onSuccess();
          } else {
            toast.error(
              <ToastErrorMessage>
                {getError({ errors: res.errors })}
              </ToastErrorMessage>
            );
          }
        } catch (error) {
          toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
        }
      },
    };
    dispatch(OpenConfirmationAction(confirmation));
  };

  const handleCancel = async () => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Are you sure you want to cancel this amendment?',
      submitButtonText: 'Submit',
      cancelButtonText: 'Cancel',
      callback: async () => {
        try {
          const res = await cancelAmendment({
            variables: {
              selectedAmendmentID: generalData.amendment.id,
              selectedPolicyVersionID: generalData.policy.versionId,
            },
            errorPolicy: 'all',
          });

          if (!res.errors) {
            toast.success(
              <ToastSuccessMessage>
                Amendment successfully canceled
              </ToastSuccessMessage>
            );
            onSuccess();
          } else {
            toast.error(
              <ToastErrorMessage>
                {getError({ errors: res.errors })}
              </ToastErrorMessage>
            );
          }
        } catch (error) {
          toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
        }
      },
    };
    dispatch(OpenConfirmationAction(confirmation));
  };

  const actions: IEnhancedMenuItem[] = [
    {
      name: 'calculate&Save',
      title: 'Calculate & Save',
      onClick: () => handleCalculateAndSave(),
      isPrimary: true,
      disabled: calculateLoading || confirmLoading || cancelLoading,
      hidden: generalData.amendment.type === 'POLICY_CANCELLATION',
    },
    ...(generalData.amendment.status === 'IN_PROGRESS'
      ? [
          {
            name: 'confirm',
            title: 'Confirm',
            onClick: () => {
              handleConfirmAmendment();
            },
            isPrimary: true,
            disabled: calculateLoading || confirmLoading || cancelLoading,
            hidden: generalData.amendment.type === 'POLICY_CANCELLATION',
          },
        ]
      : []),
    {
      name: 'confirmCancellation',
      title: 'Confirm',
      disabled: confirmCancellationLoading || cancelLoading,
      onClick: () => handleCancellationConfirmAmendment(),
      isPrimary: true,
      hidden: generalData.amendment.type !== 'POLICY_CANCELLATION',
    },
    {
      name: 'cancel',
      title: 'Cancel',
      disabled:
        calculateLoading ||
        confirmLoading ||
        cancelLoading ||
        confirmCancellationLoading,
      onClick: () => handleCancel(),
      isPrimary: true,
    },
  ];

  return (
    <>
      <SimpleActionBar items={actions} />
      {isModalOpen && (
        <PreviewClauseModal
          generalData={generalData}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            onSuccess();
          }}
        />
      )}
    </>
  );
};

export default StepperActions;
